import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Typography,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Security,
} from '@mui/icons-material';
import { updatePassword } from 'aws-amplify/auth';
import logger from '../../utils/logger';
import { authService } from '../../services/AuthService';
import type { PasswordPolicy } from '../../../../shared/src/schemas';
import { getUnknownErrorMessage, getUnknownErrorName } from '../../utils/unknownError';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

/**
 * Generate password requirements based on Cognito policy
 */
const generatePasswordRequirements = (policy: PasswordPolicy): PasswordRequirement[] => {
  const requirements: PasswordRequirement[] = [];

  requirements.push({
    label: `At least ${policy.minimumLength} characters`,
    test: (pwd) => pwd.length >= policy.minimumLength,
  });

  if (policy.requireUppercase) {
    requirements.push({
      label: 'Contains uppercase letter',
      test: (pwd) => /[A-Z]/.test(pwd),
    });
  }

  if (policy.requireLowercase) {
    requirements.push({
      label: 'Contains lowercase letter',
      test: (pwd) => /[a-z]/.test(pwd),
    });
  }

  if (policy.requireNumbers) {
    requirements.push({
      label: 'Contains number',
      test: (pwd) => /[0-9]/.test(pwd),
    });
  }

  if (policy.requireSymbols) {
    requirements.push({
      label: 'Contains special character',
      test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    });
  }

  return requirements;
};

/**
 * Change Password Dialog Component
 * 
 * Allows authenticated users to change their password using AWS Amplify/Cognito.
 * This component uses the client-side Amplify API for secure password changes.
 * Password requirements are fetched dynamically from Cognito User Pool configuration.
 * 
 * Features:
 * - Dynamic password requirements from AWS Cognito
 * - Real-time password strength validation
 * - Show/hide password toggle
 * - Visual feedback for password requirements
 * - Error handling and user feedback
 * - Automatic logout after successful password change (optional)
 */
export default function ChangePasswordDialog({
  open,
  onClose,
  onSuccess,
}: ChangePasswordDialogProps): React.ReactElement {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(true);

  // Fetch password policy from backend when dialog opens
  useEffect(() => {
    if (open) {
      const fetchPasswordPolicy = async () => {
        try {
          setLoadingPolicy(true);
          const policy = await authService.getPasswordPolicy();
          setPasswordPolicy(policy);
        } catch (err) {
          logger.error('Failed to fetch password policy', { error: err });
          // Use default policy if fetch fails
          setPasswordPolicy({
            minimumLength: 8,
            requireUppercase: true,
            requireLowercase: true,
            requireNumbers: true,
            requireSymbols: true,
            temporaryPasswordValidityDays: 7,
          });
        } finally {
          setLoadingPolicy(false);
        }
      };

      fetchPasswordPolicy();
    }
  }, [open]);

  // Generate requirements based on fetched policy
  const passwordRequirements = passwordPolicy 
    ? generatePasswordRequirements(passwordPolicy) 
    : [];

  // Check if new password meets all requirements
  const passwordMeetsRequirements = passwordRequirements.every((req) =>
    req.test(newPassword)
  );

  // Check if passwords match
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  // Form is valid if all conditions are met
  const isFormValid =
    currentPassword.trim() !== '' &&
    newPassword.trim() !== '' &&
    confirmPassword.trim() !== '' &&
    passwordMeetsRequirements &&
    passwordsMatch;

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      setError('Please ensure all requirements are met');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use Amplify's updatePassword API
      // This requires the user to be authenticated and provide their current password
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      logger.info('Password changed successfully');
      setSuccess(true);

      // Wait a moment to show success message
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }, 2000);
    } catch (err: unknown) {
      logger.error('Password change failed', { error: getUnknownErrorMessage(err, 'Unknown error') });

      // Handle specific Cognito errors
      let errorMessage = 'Failed to change password. Please try again.';
      const errorName = getUnknownErrorName(err);

      if (errorName === 'NotAuthorizedException') {
        errorMessage = 'Current password is incorrect';
      } else if (errorName === 'InvalidPasswordException') {
        errorMessage = 'New password does not meet requirements';
      } else if (errorName === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later';
      } else {
        errorMessage = getUnknownErrorMessage(err, errorMessage);
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color="primary" />
          <Typography variant="h6">Change Password</Typography>
        </Box>
        <AppIconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          disabled={loading}
        >
          <CloseIcon />
        </AppIconButton>
      </DialogTitle>

      {(loading || loadingPolicy) && <LinearProgress />}

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {loadingPolicy && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading password requirements...
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password changed successfully! You can now use your new password to sign in.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            For your security, please enter your current password and choose a strong new password.
          </Typography>

          {/* Current Password */}
          <AppTextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading || success || loadingPolicy}
            required
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AppIconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    disabled={loading || success}
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </AppIconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* New Password */}
          <AppTextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading || success || loadingPolicy}
            required
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AppIconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    disabled={loading || success || loadingPolicy}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </AppIconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Requirements */}
          {newPassword && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Password Requirements:
              </Typography>
              <List dense disablePadding>
                {passwordRequirements.map((req, index) => {
                  const met = req.test(newPassword);
                  return (
                    <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {met ? (
                          <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <Cancel sx={{ fontSize: 18, color: 'error.main' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: met ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Confirm Password */}
          <AppTextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading || success || loadingPolicy}
            required
            error={confirmPassword !== '' && !passwordsMatch}
            helperText={
              confirmPassword !== '' && !passwordsMatch ? 'Passwords do not match' : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AppIconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading || success || loadingPolicy}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </AppIconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <AppButton onClick={handleClose} disabled={loading || loadingPolicy} color="inherit">
            Cancel
          </AppButton>
          <AppButton
            type="submit"
            variant="contained"
            disabled={!isFormValid || loading || success || loadingPolicy}
            startIcon={<Security />}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </AppButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
